import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  Grid,
  GridItem,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { MdCheckCircle, MdExitToApp } from 'react-icons/md';

import CompanySignupApi from '../../../../api/companySignup';

const SuccessStep = ({ data }) => {
  // State for fetched company data
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Color mode values - MUST be called at top level before any early returns
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = useColorModeValue('gray.400', 'white');
  const successColor = useColorModeValue('green.500', 'green.400');

  // Fetch company data from database when component mounts
  useEffect(() => {
    const fetchCompanyData = async () => {
      console.log('SuccessStep data received:', data);

      if (!data?.companyId) {
        console.error('No companyId found in data:', data);
        setError('מספר החברה לא זמין');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await CompanySignupApi.getCompanySignupData(data.companyId);

        if (result.success) {
          setCompanyData(result.data);
          setError(null);
        } else {
          setError(result.errorMessage || 'שגיאה בטעינת נתוני החברה');
        }
      } catch (err) {
        console.error('Error fetching company data:', err);
        setError('שגיאה בטעינת נתוני החברה');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [data]);

  const handleGoToDashboard = () => {
    // Navigate to the dashboard using hash routing for production compatibility
    window.location.href = '/#/rtl';
  };

  // Show loading state
  if (loading) {
    return (
      <VStack spacing={6} align="center" textAlign="center" py={8}>
        <Spinner size="xl" color={successColor} />
        <Text fontSize="lg" color={textColorSecondary}>
          טוען נתוני החברה...
        </Text>
      </VStack>
    );
  }

  // Show error state
  if (error) {
    return (
      <VStack spacing={6} align="stretch" textAlign="center">
        <Alert status="error" borderRadius="12px">
          <AlertIcon />
          {error}
        </Alert>
        <Button
          onClick={() => window.location.reload()}
          size="lg"
          colorScheme="blue"
          variant="outline"
        >
          נסה שוב
        </Button>
      </VStack>
    );
  }

  // Use fetched data or fallback to form data
  const displayData = companyData || data;


  return (
    <VStack spacing={6} align="stretch" textAlign="center">
      {/* Success Icon and Title */}
      <Box mb={4}>
        <Box
          w="20"
          h="20"
          bg="teal.500"
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mx="auto"
          mb={4}
          animation="scale-in 0.5s ease-out"
        >
          <MdCheckCircle size="48px" color="white" />
        </Box>
        <Text
          fontSize={{ base: '24px', md: '32px' }}
          fontWeight="700"
          color="teal.500"
          mb={2}
        >
          ברוכים הבאים ל-Payro!
        </Text>
        <Text
          fontSize="lg"
          color={textColorSecondary}
          fontWeight="400"
        >
          תהליך ההצטרפות הושלם בהצלחה
        </Text>
      </Box>

      {/* Summary Card */}
      <Box
        bg="teal.50"
        border="1px solid"
        borderColor="teal.100"
        borderRadius="12px"
        p={6}
      >
        <Text fontSize="xl" fontWeight="600" color={textColor} mb={4}>
          פרטי משתמש admin:
        </Text>
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} textAlign="right">
          <GridItem>
            <Text fontSize="sm" color={textColorSecondary} mb={1}>
              שם החברה
            </Text>
            <Text fontWeight="600" color={textColor}>
              {displayData?.companyName || 'לא זמין'}
            </Text>
          </GridItem>
          <GridItem>
            <Text fontSize="sm" color={textColorSecondary} mb={1}>
              איש קשר
            </Text>
            <Text fontWeight="600" color={textColor}>
              {displayData?.contactName || displayData?.fullName || 'לא זמין'}
            </Text>
          </GridItem>
          <GridItem>
            <Text fontSize="sm" color={textColorSecondary} mb={1}>
              אימייל
            </Text>
            <Text fontWeight="600" color={textColor} dir="ltr">
              {displayData?.contactEmail || displayData?.email || 'לא זמין'}
            </Text>
          </GridItem>
          <GridItem>
            <Text fontSize="sm" color={textColorSecondary} mb={1}>
              מספר החברה
            </Text>
            <Text fontWeight="600" color={textColor} dir="ltr">
              {displayData?.companyId || 'לא זמין'}
            </Text>
          </GridItem>
        </Grid>
      </Box>



      {/* Next Steps */}
      <Box
        bg="teal.50"
        border="1px solid"
        borderColor="teal.100"
        borderRadius="12px"
        p={6}
      >
        <Text fontWeight="600" color={textColor} mb={3} textAlign="center">
          מה קורה הלאה?
        </Text>
        <VStack spacing={2} textAlign="right">
          <Text fontSize="sm" color={textColorSecondary}>
            מנהל/ת לקוחות יצרו עמך קשר בתוך 24 שעות ונצא לדרך!
          </Text>
        </VStack>
      </Box>

      {/* Dashboard Button */}
      <Box pt={6}>
        <Button
          onClick={handleGoToDashboard}
          size="lg"
          bg="teal.500"
          color="white"
          _hover={{ bg: 'teal.600' }}
          _active={{ bg: 'teal.700' }}
          fontSize="lg"
          px={8}
          py={3}
          minW="200px"
        >
          כניסה לפאנל הניהול
          <MdExitToApp style={{ marginRight: '8px' }} />
        </Button>
      </Box>
    </VStack>
  );
};

export default SuccessStep;
